import { create } from '@bufbuild/protobuf';
import useAuthService from '@grpc/composables/useAuthService';
import useCloudService from '@grpc/composables/useCloudService';
import useCloudServiceV2 from '@grpc/composables/useCloudServiceV2';
import usePaymentService from '@grpc/composables/usePaymentService';
import type { Client } from 'gpcore-grpc/gpcore/api/auth/v1/client_pb';
import {
  CreateClientRequestSchema,
  DeleteClientRequestSchema,
  ListClientsRequestSchema,
  type RegisterRequest,
  ResendConfirmEMailRequestSchema,
  ResetClientSecretRequestSchema,
  UpdateClientRequestSchema,
} from 'gpcore-grpc/gpcore/api/auth/v1/requests_pb';
import type {
  CreateClientResponse,
  DeleteClientResponse,
  ListClientsResponse,
  RegisterResponse,
  ResendConfirmEMailResponse,
  ResetClientSecretResponse,
  UpdateClientResponse,
} from 'gpcore-grpc/gpcore/api/auth/v1/responses_pb';
import type { BillingProfile } from 'gpcore-grpc/gpcore/api/cloud/v1/billing_pb';
import { type UserNodeLimit } from 'gpcore-grpc/gpcore/api/cloud/v1/node_pb';
import {
  CreateUserSSHKeyRequestSchema,
  DeleteUserSSHKeyRequestSchema,
  GetBillingProfileBillDetailsRequestSchema,
  GetBillingProfileBillPdfRequestSchema,
  GetUserNodeLimitRequestSchema,
  ListTimezonesRequestSchema,
  ListUserSSHKeysRequestSchema,
  UpdateTimezoneRequestSchema,
} from 'gpcore-grpc/gpcore/api/cloud/v1/requests_pb';
import type {
  CreateUserSSHKeyResponse,
  DeleteUserSSHKeyResponse,
  GetBillingProfileBillDetailsResponse,
  GetBillingProfileBillPdfResponse,
  GetUserNodeLimitResponse,
  ListTimezonesResponse,
  ListUserSSHKeysResponse,
  UpdateTimezoneResponse,
} from 'gpcore-grpc/gpcore/api/cloud/v1/responses_pb';
import {
  ListBillingProfileBillsRequestSchema,
  ListBillingProfileProjectsRequestSchema,
} from 'gpcore-grpc/gpcore/api/cloud/v2/requests_pb';
import type {
  ListBillingProfileBillsResponse,
  ListBillingProfileProjectsResponse,
} from 'gpcore-grpc/gpcore/api/cloud/v2/responses_pb';
import {
  CreateBillingProfileRequestSchema,
  DeleteBillingProfileRequestSchema,
  ListBillingProfilesRequestSchema,
  UpdateBillingProfileRequestSchema,
} from 'gpcore-grpc/gpcore/api/payment/v1/requests_pb';
import type {
  CreateBillingProfileResponse,
  DeleteBillingProfileResponse,
  ListBillingProfilesResponse,
  UpdateBillingProfileResponse,
} from 'gpcore-grpc/gpcore/api/payment/v1/responses_pb';
import { PaginationRequestSchema } from 'gpcore-grpc/gpcore/type/v1/pagination_pb';
import type { SSHKey } from 'gpcore-grpc/gpcore/type/v1/ssh_key_pb';
import { defineStore } from 'pinia';

export interface CreateLongLivedToken {
  password: string;
  description: string;
}

export interface CreateSSHKey {
  name: string;
  publicKey: string;
}

export interface CreateBillingProfile {
  company: string;
  name: string;
  email?: string;

  vatId: string;

  street: string;
  postcode: string;
  city: string;
  state: string;

  countryCode: string;
}

export interface UpdateBillingProfile {
  id: string;

  company: string;
  name: string;
  email?: string;

  vatId: string;

  street: string;
  postcode: string;
  city: string;
  state: string;

  countryCode: string;
}

export interface CreateClient {
  name: string;
  description: string;
}

export interface UserState {
  sshKeys: Array<SSHKey>;
  billingProfiles: Array<BillingProfile>;
  nodeLimit?: UserNodeLimit;
}

export const useUserStore = defineStore('user', {
  state: () =>
    ({
      sshKeys: new Array<SSHKey>(),
      billingProfiles: new Array<BillingProfile>(),
      nodeLimit: undefined,
    }) as UserState,
  actions: {
    removeSSHKey(id: string) {
      const existingIndex = this.sshKeys.findIndex((s) => s.id === id);
      if (existingIndex > -1) {
        this.sshKeys.splice(existingIndex, 1);
      }
    },
    setSSHKey(key: SSHKey) {
      const existingIndex = this.sshKeys.findIndex((s) => s.id === key.id);
      if (existingIndex > -1) {
        this.sshKeys[existingIndex] = key;
      } else {
        this.sshKeys.unshift(key);
      }
    },
    removeBillingProfile(id: string) {
      const existingIndex = this.billingProfiles.findIndex((s) => s.id === id);
      if (existingIndex > -1) {
        this.billingProfiles.splice(existingIndex, 1);
      }
    },
    setBillingProfile(profile: BillingProfile) {
      const existingIndex = this.billingProfiles.findIndex((s) => s.id === profile.id);
      if (existingIndex > -1) {
        this.billingProfiles[existingIndex] = profile;
      } else {
        this.billingProfiles.unshift(profile);
      }
    },
    async register(request: RegisterRequest): Promise<RegisterResponse> {
      const authService = useAuthService();
      const { response, error } = await authService.executeApiCall('register', request);
      if (error != null) throw error;

      return response;
    },
    async resendConfirmEMail(): Promise<ResendConfirmEMailResponse> {
      const authService = useAuthService();
      const request = create(ResendConfirmEMailRequestSchema);

      const { response, error } = await authService.executeApiCall('resendConfirmEMail', request);
      if (error != null) throw error;

      return response;
    },
    async reloadSSHKeys(): Promise<ListUserSSHKeysResponse> {
      const cloudService = useCloudService();
      const request = create(ListUserSSHKeysRequestSchema);
      // Pagination is ignored in the backend, so no need to handle it here.

      const { response, error } = await cloudService.executeApiCall('listUserSSHKeys', request);
      if (error != null) throw error;

      this.sshKeys = response.sshKeys;

      return response;
    },
    async deleteSSHKey(id: string): Promise<DeleteUserSSHKeyResponse> {
      const cloudService = useCloudService();

      const request = create(DeleteUserSSHKeyRequestSchema);
      request.id = id;

      const { response, error } = await cloudService.executeApiCall('deleteUserSSHKey', request);
      if (error != null) throw error;
      this.removeSSHKey(id);

      return response;
    },
    async createSSHKey(key: CreateSSHKey): Promise<CreateUserSSHKeyResponse> {
      const cloudService = useCloudService();

      const request = create(CreateUserSSHKeyRequestSchema);
      request.name = key.name;
      request.publicKey = key.publicKey;

      const { response, error } = await cloudService.executeApiCall('createUserSSHKey', request);
      if (error != null) throw error;

      this.setSSHKey(response.sshKey);

      return response;
    },
    async reloadNodeLimit(): Promise<GetUserNodeLimitResponse> {
      const request = create(GetUserNodeLimitRequestSchema);
      const cloudService = useCloudService();
      const { response, error } = await cloudService.executeApiCall('getUserNodeLimit', request);
      if (error != null) throw error;

      this.nodeLimit = response.userLimit;

      return response;
    },
    async reloadBillingProfiles(): Promise<ListBillingProfilesResponse> {
      const paymentService = usePaymentService();

      const request = create(ListBillingProfilesRequestSchema);
      request.pagination = create(PaginationRequestSchema);
      request.pagination.limit = 100;
      request.pagination.page = 1;

      const { response, error } = await paymentService.executeApiCall(
        'listBillingProfiles',
        request,
      );
      if (error != null) throw error;

      this.billingProfiles = response.billingProfiles;

      return response;
    },
    async deleteBillingProfile(id: string): Promise<DeleteBillingProfileResponse> {
      const request = create(DeleteBillingProfileRequestSchema);
      request.id = id;

      const paymentService = usePaymentService();
      const { response, error } = await paymentService.executeApiCall(
        'deleteBillingProfile',
        request,
      );
      if (error != null) throw error;

      this.removeBillingProfile(id);

      return response;
    },
    async createBillingProfile(
      profile: CreateBillingProfile,
    ): Promise<CreateBillingProfileResponse> {
      const request = create(CreateBillingProfileRequestSchema);
      request.company = profile.company;
      request.vatId = profile.vatId;
      request.name = profile.name;
      request.street = profile.street;
      request.postcode = profile.postcode;
      request.city = profile.city;
      request.state = profile.state;
      request.countryCode = profile.countryCode;
      if (profile.email !== undefined && profile.email !== '') {
        request.billingEmail = profile.email;
      }

      const paymentService = usePaymentService();
      const { response, error } = await paymentService.executeApiCall(
        'createBillingProfile',
        request,
      );
      if (error != null) throw error;
      this.setBillingProfile(response.billingProfile);

      return response;
    },
    async updateBillingProfile(
      profile: UpdateBillingProfile,
    ): Promise<UpdateBillingProfileResponse> {
      const request = create(UpdateBillingProfileRequestSchema);
      request.id = profile.id;
      request.company = profile.company;
      request.vatId = profile.vatId;
      request.name = profile.name;
      request.street = profile.street;
      request.postcode = profile.postcode;
      request.city = profile.city;
      request.state = profile.state;
      request.countryCode = profile.countryCode;
      if (profile.email !== undefined && profile.email !== '') {
        request.billingEmail = profile.email;
      }

      const paymentService = usePaymentService();
      const { response, error } = await paymentService.executeApiCall(
        'updateBillingProfile',
        request,
      );
      if (error != null) throw error;

      this.setBillingProfile(response.billingProfile);

      return response;
    },
    async reloadBillingProfileBills({
      id,
      page,
      itemsPerPage,
      year,
    }: {
      id: string;
      page: number;
      itemsPerPage: number;
      year: number;
    }): Promise<ListBillingProfileBillsResponse> {
      const request = create(ListBillingProfileBillsRequestSchema);
      request.id = id;
      const pagination = create(PaginationRequestSchema);
      pagination.page = page;
      pagination.limit = itemsPerPage;
      request.pagination = pagination;
      request.year = year;

      const cloudServiceV2 = useCloudServiceV2();
      const { response, error } = await cloudServiceV2.executeApiCall(
        'listBillingProfileBills',
        request,
      );

      if (error != null) throw error;

      return response;
    },
    async reloadBillingProfileProjects({
      id,
      page,
      itemsPerPage,
    }: {
      id: string;
      page: number;
      itemsPerPage: number;
    }): Promise<ListBillingProfileProjectsResponse> {
      const request = create(ListBillingProfileProjectsRequestSchema);
      request.id = id;
      const pagination = create(PaginationRequestSchema);
      pagination.page = page;
      pagination.limit = itemsPerPage;
      request.pagination = pagination;

      const cloudServiceV2 = useCloudServiceV2();
      const { response, error } = await cloudServiceV2.executeApiCall(
        'listBillingProfileProjects',
        request,
      );

      if (error != null) throw error;

      return response;
    },
    async getBillDetails({
      billingProfileId,
      billId,
    }: {
      billingProfileId: string;
      billId: string;
    }): Promise<GetBillingProfileBillDetailsResponse> {
      const request = create(GetBillingProfileBillDetailsRequestSchema);
      request.id = billingProfileId;
      request.billId = billId;

      const cloudService = useCloudService();
      const { response, error } = await cloudService.executeApiCall(
        'getBillingProfileBillDetails',
        request,
      );

      if (error != null) throw error;

      return response;
    },
    async downloadBillPdf({
      billingProfileId,
      billId,
    }: {
      billingProfileId: string;
      billId: string;
    }): Promise<GetBillingProfileBillPdfResponse> {
      const request = create(GetBillingProfileBillPdfRequestSchema);
      request.id = billingProfileId;
      request.billId = billId;

      const cloudService = useCloudService();
      const { response, error } = await cloudService.executeApiCall(
        'getBillingProfileBillPdf',
        request,
      );

      if (error != null) throw error;

      return response;
    },
    async listClients(): Promise<ListClientsResponse> {
      const authService = useAuthService();
      const request = create(ListClientsRequestSchema);
      const { response, error } = await authService.executeApiCall('listClients', request);
      if (error != null) throw error;

      return response;
    },
    async deleteClient(id: string): Promise<DeleteClientResponse> {
      const request = create(DeleteClientRequestSchema);
      request.id = id;
      const authService = useAuthService();
      const { response, error } = await authService.executeApiCall('deleteClient', request);
      if (error != null) throw error;

      return response;
    },
    async createClient(req: CreateClient): Promise<CreateClientResponse> {
      const request = create(CreateClientRequestSchema);
      request.name = req.name;
      request.description = req.description;

      const authService = useAuthService();
      const { response, error } = await authService.executeApiCall('createClient', request);
      if (error != null) throw error;

      return response;
    },
    async resetClientSecret(id: string): Promise<ResetClientSecretResponse> {
      const request = create(ResetClientSecretRequestSchema);
      request.id = id;

      const authService = useAuthService();
      const { response, error } = await authService.executeApiCall('resetClientSecret', request);
      if (error != null) throw error;

      return response;
    },
    async updateClient(client: Client): Promise<UpdateClientResponse> {
      const request = create(UpdateClientRequestSchema);
      request.id = client.id;
      request.name = client.name;
      request.description = client.description;

      const authService = useAuthService();
      const { response, error } = await authService.executeApiCall('updateClient', request);
      if (error != null) throw error;

      return response;
    },
    async listTimezones(): Promise<ListTimezonesResponse> {
      const cloudService = useCloudService();
      const request = create(ListTimezonesRequestSchema);
      const { response, error } = await cloudService.executeApiCall('listTimezones', request);
      if (error != null) throw error;

      return response;
    },
    async updateTimezone(timezone: string): Promise<UpdateTimezoneResponse> {
      const request = create(UpdateTimezoneRequestSchema);
      request.timezone = timezone;

      const cloudService = useCloudService();
      const { response, error } = await cloudService.executeApiCall('updateTimezone', request);
      if (error != null) throw error;

      return response;
    },
  },
});
